import { useEffect, useState } from 'react';
import { Accordion, Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { api } from '../../lib/api';
import {
  CATEGORIAS, ESTADOS_ATUAIS, ESTADOS_BR, FACES, FINALIDADES, FINANCIAMENTOS, ISOLAMENTOS, LOCALIZACOES,
  MEDIDAS, OCUPACOES, PADROES, POSICOES,
} from './constantes';

// Busca avançada de imóveis — pensada pra achar UM imóvel específico combinando poucos critérios
// (tipo + bairro + faixa de valor + dormitórios...). Cada campo é opcional; só o que for
// preenchido vira filtro, e todos se somam (E). A lista de parâmetros é a mesma do backend
// (`src/filtrosImovel.js`).
//
// Tipos de campo:
//   texto  → contém (sem acento, sem diferença de maiúscula; várias palavras = todas precisam aparecer)
//   numero → número simples          faixa → de/até (`nome_min` / `nome_max`)
//   lista  → uma opção              simnao → "Sim" / "Não" / qualquer
//   data   → de/até (`nome_de` / `nome_ate`)
const SIM_NAO = [['sim', 'Sim'], ['nao', 'Não']];

export const SECOES = [
  {
    id: 'ident',
    titulo: 'Identificação',
    campos: [
      { nome: 'referencia', rotulo: 'Referência', tipo: 'texto', dica: 'ex.: CH00107' },
      { nome: 'id_internet', rotulo: 'Nº do imóvel (#)', tipo: 'numero', dica: 'ex.: 692' },
      { nome: 'referencia_auxiliar', rotulo: 'Referência auxiliar', tipo: 'texto' },
      { nome: 'titulo', rotulo: 'Título', tipo: 'texto' },
      { nome: 'descricao', rotulo: 'Descrição / palavras-chave', tipo: 'texto' },
      { nome: 'descricao_interna', rotulo: 'Descrição interna', tipo: 'texto' },
      { nome: 'matricula', rotulo: 'Matrícula / IPTU / INCRA', tipo: 'texto' },
    ],
  },
  {
    id: 'tipo',
    titulo: 'Tipo e situação',
    campos: [
      { nome: 'tipo', rotulo: 'Tipo', tipo: 'lista', especial: 'tipos' },
      { nome: 'categoria', rotulo: 'Categoria', tipo: 'lista', opcoes: CATEGORIAS },
      { nome: 'finalidade', rotulo: 'Finalidade', tipo: 'lista', opcoes: FINALIDADES },
      { nome: 'situacao', rotulo: 'Situação', tipo: 'lista', opcoes: [['ativos', 'Ativos (publicados)'], ['inativos', 'Inativos'], ['rascunhos', 'Cadastro incompleto'], ['bloqueados', 'Bloqueados']] },
      { nome: 'situacao_exata', rotulo: 'Situação exata', tipo: 'lista', opcoes: ['Ativo', 'Inativo', 'Vendido', 'Locado', 'Permutado'] },
      { nome: 'estado_atual', rotulo: 'Estado do imóvel', tipo: 'lista', opcoes: ESTADOS_ATUAIS },
      { nome: 'ocupacao', rotulo: 'Ocupação', tipo: 'lista', opcoes: OCUPACOES },
      { nome: 'padrao', rotulo: 'Padrão', tipo: 'lista', opcoes: PADROES },
      { nome: 'localizacao', rotulo: 'Localização (qualidade)', tipo: 'lista', opcoes: LOCALIZACOES },
    ],
  },
  {
    id: 'valores',
    titulo: 'Valores',
    campos: [
      { nome: 'valor_venda', rotulo: 'Valor de venda (R$)', tipo: 'faixa' },
      { nome: 'valor_locacao', rotulo: 'Locação (R$)', tipo: 'faixa' },
      { nome: 'valor_temporada', rotulo: 'Temporada (R$)', tipo: 'faixa' },
      { nome: 'valor_condominio', rotulo: 'Condomínio (R$)', tipo: 'faixa' },
      { nome: 'valor_iptu', rotulo: 'IPTU (R$)', tipo: 'faixa' },
      { nome: 'preco_m2', rotulo: 'Preço por m² (R$)', tipo: 'faixa' },
      { nome: 'financiamento', rotulo: 'Financiamento', tipo: 'lista', opcoes: FINANCIAMENTOS },
      { nome: 'aceita_permuta', rotulo: 'Aceita permuta', tipo: 'simnao' },
      { nome: 'financiado', rotulo: 'Financiado', tipo: 'simnao' },
      { nome: 'valor_sob_consulta', rotulo: 'Valor sob consulta', tipo: 'simnao' },
      { nome: 'valor_negociavel', rotulo: 'Valor negociável', tipo: 'simnao' },
    ],
  },
  {
    id: 'detalhes',
    titulo: 'Cômodos e áreas',
    campos: [
      { nome: 'dormitorios', rotulo: 'Dormitórios', tipo: 'faixa' },
      { nome: 'suites', rotulo: 'Suítes', tipo: 'faixa' },
      { nome: 'banheiros', rotulo: 'Banheiros', tipo: 'faixa' },
      { nome: 'garagens', rotulo: 'Garagens', tipo: 'faixa' },
      { nome: 'salas', rotulo: 'Salas', tipo: 'faixa' },
      { nome: 'andares', rotulo: 'Andares', tipo: 'faixa' },
      { nome: 'elevadores', rotulo: 'Elevadores', tipo: 'faixa' },
      { nome: 'area_construida', rotulo: 'Área construída', tipo: 'faixa' },
      { nome: 'area_util', rotulo: 'Área útil', tipo: 'faixa' },
      { nome: 'area_terreno', rotulo: 'Área do terreno', tipo: 'faixa' },
      { nome: 'distancia_mar', rotulo: 'Distância do mar', tipo: 'faixa' },
      { nome: 'medida', rotulo: 'Unidade da medida', tipo: 'lista', opcoes: MEDIDAS },
      { nome: 'face', rotulo: 'Face', tipo: 'lista', opcoes: FACES.filter(Boolean) },
      { nome: 'isolamento', rotulo: 'Isolamento', tipo: 'lista', opcoes: ISOLAMENTOS.filter(Boolean) },
      { nome: 'posicao', rotulo: 'Posição', tipo: 'lista', opcoes: POSICOES.filter(Boolean) },
    ],
  },
  {
    id: 'local',
    titulo: 'Localização',
    campos: [
      { nome: 'estado', rotulo: 'Estado', tipo: 'lista', opcoes: ESTADOS_BR.map(([sigla, nome]) => [sigla, nome]) },
      { nome: 'cidade', rotulo: 'Cidade', tipo: 'texto' },
      { nome: 'bairro', rotulo: 'Bairro', tipo: 'texto' },
      { nome: 'endereco', rotulo: 'Endereço / rua', tipo: 'texto' },
      { nome: 'cep', rotulo: 'CEP (começa com)', tipo: 'texto', dica: 'ex.: 13400' },
      { nome: 'complemento', rotulo: 'Complemento', tipo: 'texto' },
      { nome: 'condominio', rotulo: 'Condomínio', tipo: 'texto' },
      { nome: 'em_condominio', rotulo: 'Está em condomínio', tipo: 'simnao' },
      { nome: 'mapa', rotulo: 'Ponto no mapa', tipo: 'lista', opcoes: [['com', 'Com coordenadas'], ['sem', 'Sem coordenadas']] },
    ],
  },
  {
    id: 'pessoas',
    titulo: 'Pessoas',
    campos: [
      { nome: 'proprietario', rotulo: 'Proprietário', tipo: 'texto', dica: 'nome, telefone, e-mail ou CPF/CNPJ' },
      { nome: 'captador', rotulo: 'Captador', tipo: 'lista', especial: 'captadores' },
      { nome: 'cadastrado_por', rotulo: 'Cadastrado por', tipo: 'lista', especial: 'captadores' },
    ],
  },
  {
    id: 'anuncio',
    titulo: 'Anúncio e publicação',
    campos: [
      { nome: 'destaque', rotulo: 'Destaque', tipo: 'simnao' },
      { nome: 'portais', rotulo: 'Divulgar em portais', tipo: 'simnao' },
      { nome: 'site_empresa', rotulo: 'Site da empresa', tipo: 'simnao' },
      { nome: 'site_rede', rotulo: 'Site da rede', tipo: 'simnao' },
      { nome: 'oportunidade', rotulo: 'Oportunidade de negócio', tipo: 'simnao' },
      { nome: 'tv', rotulo: 'TV', tipo: 'simnao' },
      { nome: 'autorizado', rotulo: 'Autorizado para publicar', tipo: 'simnao' },
      { nome: 'documentacao_pendente', rotulo: 'Documentação pendente', tipo: 'simnao' },
      { nome: 'em_parceria', rotulo: 'Em parceria', tipo: 'simnao' },
      { nome: 'fotos', rotulo: 'Fotos', tipo: 'lista', opcoes: [['sem', 'Sem fotos'], ['menos2', 'Menos de 2 (fora dos portais)'], ['com2', '2 ou mais (nos portais)']] },
    ],
  },
  {
    id: 'datas',
    titulo: 'Datas e ordenação',
    campos: [
      { nome: 'criado', rotulo: 'Cadastrado', tipo: 'data' },
      { nome: 'atualizado', rotulo: 'Atualizado', tipo: 'data' },
      {
        nome: 'ordem',
        rotulo: 'Ordenar por',
        tipo: 'lista',
        opcoes: [['recentes', 'Mais recentes'], ['antigos', 'Mais antigos'], ['atualizados', 'Atualizados recentemente'], ['maior_valor', 'Maior valor'], ['menor_valor', 'Menor valor'], ['referencia', 'Referência'], ['maior_area', 'Maior área construída']],
      },
    ],
  },
];

// nomes de parâmetro que um campo gera
function parametrosDoCampo(campo) {
  if (campo.tipo === 'faixa') return [`${campo.nome}_min`, `${campo.nome}_max`];
  if (campo.tipo === 'data') return [`${campo.nome}_de`, `${campo.nome}_ate`];
  return [campo.nome];
}

const normalizarOpcao = (o) => (Array.isArray(o) ? o : [o, o]);

// Resumo dos filtros ativos (chips da lista): [{ chaves: [...], texto }]
export function descreverFiltros(filtros, { tipos = [], captadores = [], caracteristicas = [] } = {}) {
  const saida = [];
  for (const secao of SECOES) {
    for (const campo of secao.campos) {
      const chaves = parametrosDoCampo(campo).filter((k) => filtros[k] !== undefined && filtros[k] !== '');
      if (chaves.length === 0) continue;
      let valor;
      if (campo.tipo === 'faixa' || campo.tipo === 'data') {
        const [a, b] = parametrosDoCampo(campo);
        const de = filtros[a];
        const ate = filtros[b];
        valor = de && ate ? `${de} a ${ate}` : de ? `a partir de ${de}` : `até ${ate}`;
      } else if (campo.tipo === 'simnao') {
        valor = filtros[campo.nome] === 'sim' ? 'Sim' : 'Não';
      } else if (campo.especial === 'tipos') {
        valor = tipos.find((t) => String(t.id) === String(filtros[campo.nome]))?.descricao || filtros[campo.nome];
      } else if (campo.especial === 'captadores') {
        valor = captadores.find((c) => c.public_id === filtros[campo.nome])?.name || filtros[campo.nome];
      } else if (campo.opcoes) {
        valor = normalizarOpcao(campo.opcoes.find((o) => normalizarOpcao(o)[0] === filtros[campo.nome]) ?? filtros[campo.nome])[1];
      } else {
        valor = filtros[campo.nome];
      }
      saida.push({ chaves, texto: `${campo.rotulo}: ${valor}` });
    }
  }
  if (filtros.caracteristicas) {
    const ids = String(filtros.caracteristicas).split(',');
    const nomes = ids.map((id) => caracteristicas.find((c) => String(c.id) === id)?.descricao).filter(Boolean);
    saida.push({ chaves: ['caracteristicas'], texto: `Características: ${nomes.length ? nomes.join(', ') : `${ids.length} marcada(s)`}` });
  }
  return saida;
}

export default function BuscaAvancadaImoveis({ valores, tipos, captadores, aoAplicar, aoLimpar }) {
  const [v, setV] = useState(valores || {});
  const [grupos, setGrupos] = useState(null); // características do tipo escolhido
  const [carregandoCarac, setCarregandoCarac] = useState(false);

  // quando o pai troca os filtros (chip removido, "limpar"), o formulário acompanha
  useEffect(() => setV(valores || {}), [valores]);

  const marcadas = new Set(String(v.caracteristicas || '').split(',').filter(Boolean));

  // características só fazem sentido pra um TIPO (cada tipo tem as suas)
  useEffect(() => {
    if (!v.tipo) {
      setGrupos(null);
      return undefined;
    }
    let cancelado = false;
    setCarregandoCarac(true);
    api
      .get(`/tipos/${v.tipo}/caracteristicas`)
      .then((r) => !cancelado && setGrupos(r.data))
      .catch(() => !cancelado && setGrupos([]))
      .finally(() => !cancelado && setCarregandoCarac(false));
    return () => {
      cancelado = true;
    };
  }, [v.tipo]);

  const mudar = (nome, valor) => setV((atual) => ({ ...atual, [nome]: valor }));

  function alternarCaracteristica(id, ligada) {
    const novo = new Set(marcadas);
    if (ligada) novo.add(String(id));
    else novo.delete(String(id));
    mudar('caracteristicas', Array.from(novo).join(','));
  }

  function aplicar(e) {
    e.preventDefault();
    const limpo = {};
    for (const [k, val] of Object.entries(v)) if (val !== '' && val !== undefined && val !== null) limpo[k] = val;
    // características de outro tipo não valem se o tipo foi tirado
    if (!limpo.tipo) delete limpo.caracteristicas;
    aoAplicar(limpo);
  }

  function renderCampo(campo) {
    const opcoes =
      campo.especial === 'tipos'
        ? tipos.map((t) => [String(t.id), t.descricao])
        : campo.especial === 'captadores'
          ? captadores.map((c) => [c.public_id, c.name])
          : (campo.opcoes || []).map(normalizarOpcao);

    if (campo.tipo === 'faixa') {
      return (
        <Col xs={12} sm={6} lg={4} key={campo.nome}>
          <Form.Label className="small mb-1">{campo.rotulo}</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control size="sm" type="number" min="0" step="any" inputMode="decimal" placeholder="de" aria-label={`${campo.rotulo} de`} value={v[`${campo.nome}_min`] ?? ''} onChange={(e) => mudar(`${campo.nome}_min`, e.target.value)} />
            <Form.Control size="sm" type="number" min="0" step="any" inputMode="decimal" placeholder="até" aria-label={`${campo.rotulo} até`} value={v[`${campo.nome}_max`] ?? ''} onChange={(e) => mudar(`${campo.nome}_max`, e.target.value)} />
          </div>
        </Col>
      );
    }
    if (campo.tipo === 'data') {
      return (
        <Col xs={12} sm={6} lg={4} key={campo.nome}>
          <Form.Label className="small mb-1">{campo.rotulo}</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control size="sm" type="date" aria-label={`${campo.rotulo} de`} value={v[`${campo.nome}_de`] ?? ''} onChange={(e) => mudar(`${campo.nome}_de`, e.target.value)} />
            <Form.Control size="sm" type="date" aria-label={`${campo.rotulo} até`} value={v[`${campo.nome}_ate`] ?? ''} onChange={(e) => mudar(`${campo.nome}_ate`, e.target.value)} />
          </div>
        </Col>
      );
    }
    const rotuloEId = `av-${campo.nome}`;
    return (
      <Col xs={12} sm={6} lg={4} key={campo.nome}>
        <Form.Group controlId={rotuloEId}>
          <Form.Label className="small mb-1">{campo.rotulo}</Form.Label>
          {campo.tipo === 'lista' || campo.tipo === 'simnao' ? (
            <Form.Select size="sm" value={v[campo.nome] ?? ''} onChange={(e) => mudar(campo.nome, e.target.value)}>
              <option value="">{campo.tipo === 'simnao' ? 'Tanto faz' : 'Qualquer'}</option>
              {(campo.tipo === 'simnao' ? SIM_NAO : opcoes).map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control
              size="sm"
              type={campo.tipo === 'numero' ? 'number' : 'text'}
              min={campo.tipo === 'numero' ? '0' : undefined}
              placeholder={campo.dica}
              autoComplete="off"
              value={v[campo.nome] ?? ''}
              onChange={(e) => mudar(campo.nome, e.target.value)}
            />
          )}
        </Form.Group>
      </Col>
    );
  }

  const totalPreenchidos = Object.values(v).filter((x) => x !== '' && x !== undefined && x !== null).length;

  return (
    <Form onSubmit={aplicar} className="border rounded p-3 mb-3 bg-light">
      <Accordion defaultActiveKey={['tipo', 'valores']} alwaysOpen flush className="mb-3">
        {SECOES.map((secao) => (
          <Accordion.Item eventKey={secao.id} key={secao.id}>
            <Accordion.Header>{secao.titulo}</Accordion.Header>
            <Accordion.Body>
              <Row className="g-2">{secao.campos.map(renderCampo)}</Row>

              {secao.id === 'detalhes' && (
                <div className="mt-3">
                  <div className="small fw-semibold mb-1">Características (o imóvel precisa ter TODAS as marcadas)</div>
                  {!v.tipo && <div className="text-muted small">Escolha um tipo (em "Tipo e situação") para ver as características dele.</div>}
                  {carregandoCarac && <Spinner animation="border" size="sm" />}
                  {grupos?.map((grupo) => (
                    <div key={grupo.nivel_id} className="mb-2">
                      <div className="small text-muted border-bottom mb-1">{grupo.nivel}</div>
                      <Row className="g-1">
                        {grupo.itens.map((item) => (
                          <Col xs={12} sm={6} lg={4} xl={3} key={item.id}>
                            <Form.Check type="checkbox" id={`av-carac-${item.id}`} className="small" label={item.descricao} checked={marcadas.has(String(item.id))} onChange={(e) => alternarCaracteristica(item.id, e.target.checked)} />
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <div className="d-flex flex-wrap gap-2 align-items-center">
        <Button type="submit" variant="primary">
          Buscar imóveis
        </Button>
        <Button
          type="button"
          variant="outline-secondary"
          onClick={() => {
            setV({});
            aoLimpar();
          }}
        >
          Limpar tudo
        </Button>
        <span className="text-muted small">{totalPreenchidos === 0 ? 'Preencha só o que você sabe do imóvel — todos os campos se somam.' : `${totalPreenchidos} critério(s) preenchido(s)`}</span>
      </div>
    </Form>
  );
}
